import { useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import ImagePreview from './ImagePreview';

/** 正文图片使用已有阅读器；Dialog 负责 Escape、焦点约束和关闭后的焦点恢复。 */
export default function MarkdownImage({ src, alt = '', title }: { src?: string; alt?: string; title?: string }) {
  return <MarkdownImageContent key={src} src={src} alt={alt} title={title} />;
}

/** 切换图片即卸载旧弹窗，防止流式重写后仍查看上一张图。 */
function MarkdownImageContent({ src, alt = '', title }: { src?: string; alt?: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  /** 等弹窗解除页面 inert 后再归还焦点；源图片卸载则自动跳过。 */
  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }));
  };
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (!src) return <span>{alt}</span>;
  return <>
    <button ref={trigger} type="button" title={title} aria-label={`${i18nService.t('imagePreview')}：${alt}`}
      className="my-4 inline-block max-w-full rounded-xl text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-claude-accent"
      onClick={event => {
        event.preventDefault(); event.stopPropagation();
        // macOS 鼠标点击按钮不保证获得焦点，显式记录弹窗关闭后的返回位置。
        event.currentTarget.focus();
        if (failed) { setFailed(false); setAttempt(value => value + 1); }
        else setOpen(true);
      }}>
      {failed ? <span role="alert" className="block rounded border p-3 text-sm">{alt} · {i18nService.t('imageLoadFailed')}</span>
        : <img key={attempt} src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)}
            className="max-w-full h-auto rounded-xl cursor-zoom-in" />}
    </button>
    <Dialog open={open} onClose={close} className="fixed inset-0 z-[160]">
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-3">
        <Dialog.Panel className="flex h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-[#1a1a1a] text-white">
          <div className="flex shrink-0 items-center gap-3 border-b border-white/20 p-3">
            <Dialog.Title className="min-w-0 flex-1 truncate">{alt || i18nService.t('imagePreview')}</Dialog.Title>
            <button type="button" aria-label={i18nService.t('close')} onClick={close} className="rounded p-2 hover:bg-white/10">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1"><ImagePreview dataUrl={src} fileName={alt} /></div>
        </Dialog.Panel>
      </div>
    </Dialog>
  </>;
}
